import { Box, Typography, useTheme } from '@mui/material';
import CustomImageComponent from '../shared/custom/custom-image';
import CustomLink from '../shared/custom/custom-link';
import { getMasterPhotoById, getTeamImageById } from '@/helpers/images-helper';
import ParteMedicoIcon from '../shared/icons/parte-medico-icon';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { obtenerEquipo } from '@/data/equipos';
import StatusIconComponent from '@/DesignSystem/features/status-icon/StatusIconComponent';

const NoticiaFlashRecommendations = ({ noticia }) => {
    console.log('🚀 ~ NoticiaFlashRecommendations ~ noticia:', noticia);
    const theme = useTheme();

    const renderSection = (title, content) => {
        if (!content) return null;

        return (
            <Box
                sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'background.darkCard',
                    border: `1px solid ${theme.palette.border.main}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Typography>
                </Box>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.6 }}>
                    {content}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ mt: 2 }}>
            {renderSection('Información general:', noticia?.generalStatus?.statusInfo)}
            {renderSection('Tiempo de baja estimado:', noticia?.generalStatus?.report)}
            {renderSection('Recomendaciones Fantasy:', noticia?.generalStatus?.recommendations)}
            {renderSection('Beneficiados fantasy:', noticia?.generalStatus?.beneficiados)}
            {renderSection('Perjudicados fantasy:', noticia?.generalStatus?.perjudicados)}
        </Box>
    );
};

const NoticiaFlashPlayerCard = ({ noticia }) => {
    const theme = useTheme();
    const player = noticia?.player ?? noticia?.information;
    const teamId = player?.teamId ?? noticia?.teamId ?? noticia?.team?.id ?? noticia?.team;
    const teamFound = obtenerEquipo(teamId);
    const teamName = teamFound?.name ?? player?.teamName;

    const status = noticia?.generalStatus?.status;
    const statusLevel = noticia?.generalStatus?.statusLevel;

    return (
        <CustomSectionWrapper
            color="accent3"
            sx={{
                padding: 2,
                borderRadius: 2,
                marginBottom: 2,
                bgcolor: theme.palette.background.default,
                border: `1px solid ${theme.palette.border.main}`,
            }}
        >
            {/* Player Info */}
            <Box sx={{ display: 'flex', gap: 0.5, position: 'relative', alignItems: 'center' }}>
                {/* Player Image */}
                <CustomImageComponent
                    width={80}
                    height={80}
                    src={getMasterPhotoById(player?.id, 120)}
                    alt={player?.name}
                    style={{ borderRadius: '50%' }}
                />

                {/* Player Details */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Player Name and Medical Report */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                            {player?.name ?? player?.nickname}
                        </Typography>

                        {noticia?.generalStatus?.medicalReport && (
                            <CustomLink href={noticia.generalStatus.medicalReport}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    <ParteMedicoIcon width={18} height={18} />
                                    <Typography sx={{ fontSize: '12px', color: 'primary.main' }}>
                                        Parte médico
                                    </Typography>
                                </Box>
                            </CustomLink>
                        )}
                    </Box>

                    {/* Team Info */}
                    {teamId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CustomImageComponent
                                width={24}
                                height={24}
                                src={getTeamImageById(teamId, 40)}
                                alt={teamName || 'equipo'}
                            />
                            <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                                {teamName}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Status Section */}
            <Box sx={{ mt: 2 }}>
                {/* Status Transfer */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'background.darkCard',
                        border: `1px solid ${theme.palette.border.main}`,
                        mb: 2,
                    }}
                >
                    <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 1 }}>
                        Cambio de estado:
                    </Typography>
                    {/* Previous Status */}
                    <Box display={'flex'} gap={1}>
                        <StatusIconComponent
                            status={noticia?.previous?.status}
                            statusLevel={noticia?.previous?.statusLevel}
                            useLabel={true}
                        />

                        {/* Arrow if status changed */}
                        {(noticia?.previous?.status !== status ||
                            noticia?.previous?.statusLevel !== statusLevel) && (
                            <>
                                <StatusIconComponent status="arrow" width={40} height={40} />
                                <StatusIconComponent
                                    status={status}
                                    statusLevel={statusLevel}
                                    useLabel={true}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Recommendations */}
            <NoticiaFlashRecommendations noticia={noticia} />
        </CustomSectionWrapper>
    );
};

export default NoticiaFlashPlayerCard;
